import { ForwardedRef, forwardRef } from "react";
import { useRouter } from "next/router";
import { resolveHref } from "next/dist/client/resolve-href";
import Link, { InternalLinkProps } from "components/Shared/Link";
import type { Url } from "utils/links";

export type UseIsPathCurrentProps = {
  /** Determines whether the current path must match the provided `href`
   * exactly, including query strings, or if all paths starting with
   * the provided `href` should also be considered active (default). */
  exactMatch?: boolean;
  /** Determines whether the current pathname must match the provided `pathname`
   * exactly, ignoring query strings (default), or if all paths starting with
   * the provided `pathname` should also be considered active. */
  exactPathname?: boolean;
  /** An array of pathnames to that are not to be considered current. */
  excludedPathnames?: string[];
};

function ensureArray(href: Url | Url[] | null | undefined) {
  if (href && Array.isArray(href)) return href;

  if (href) return [href];

  if (typeof href === "string") return;

  return href;
}

export function useIsPathCurrent(
  hrefs: Url | Url[] | null | undefined,
  {
    exactMatch = false,
    exactPathname = true,
    excludedPathnames = [],
  }: UseIsPathCurrentProps = {},
) {
  const router = useRouter();

  const { asPath: currentHref, pathname } = router;

  if (
    excludedPathnames.includes(currentHref) ||
    excludedPathnames.includes(pathname)
  ) {
    return false;
  }

  const hrefArr = ensureArray(hrefs);

  return hrefArr?.some((href) => {
    const resolvedHref = resolveHref(router, href, true)[1];

    if (exactMatch) return currentHref === resolvedHref;

    if (exactPathname) {
      const pathnameFromHref = currentHref.replace(/([^?]+)(\?.*)$/, "$1");
      return pathname === resolvedHref || pathnameFromHref === resolvedHref;
    }

    return (
      resolvedHref?.startsWith("/") &&
      (currentHref.startsWith(resolvedHref) ||
        pathname.startsWith(resolvedHref))
    );
  });
}

export type ActiveLinkProps = InternalLinkProps &
  UseIsPathCurrentProps & {
    /** If provided, this `href` will be used instead of the actual `href` when
     * performing the 'is current path' check. */
    hrefForCurrent?: Url | Url[];
    /** If provided, this component will bypass checking whether the link is
     * current and will operate in a 'controlled' manner based on this prop. */
    isCurrent?: boolean;
  };

function ActiveLink(
  {
    exactMatch = false,
    exactPathname = true,
    excludedPathnames,
    href,
    hrefForCurrent,
    isCurrent: isCurrentProp,
    ...rest
  }: ActiveLinkProps,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  const isCurrent = useIsPathCurrent(hrefForCurrent || href, {
    exactMatch,
    exactPathname,
    excludedPathnames,
  });
  return (
    <Link
      ref={ref}
      href={href || undefined}
      aria-current={
        (typeof isCurrentProp === "boolean" ? isCurrentProp : isCurrent)
          ? "page"
          : undefined
      }
      {...rest}
    />
  );
}

export default forwardRef(ActiveLink);
